/**
 * The per-viewer **ignore / squelch list** (client-side only). A player can ignore
 * another player or squelch a spamming NPC so that source's chat no longer shows
 * for *this* viewer - both its overhead speech bubble and its journal line are
 * suppressed, as is their player **emote animation** (the chat-triggered dance,
 * ADR-0387). Nothing here is broadcast: the server keeps delivering every line;
 * this module is the local decision to hide some of them.
 *
 * Identity is the **display name**, not the entity id: ids are per-session/boot and
 * churn on respawn, whereas a name is the durable, cross-session key (and the
 * natural UO ignore-by-name behaviour - squelching a scripted "TownCrier" keeps it
 * squelched next session). Matching is case-insensitive and trimmed; the first-seen
 * casing is kept for display.
 *
 * The set persists in `localStorage` under a `britbank.*` key, the same pattern the
 * keybindings/sound settings use ({@link ../input/keybindings}), and load-merges
 * defensively so malformed storage falls back to an empty list rather than throwing.
 * A change subscription lets the live speech filter and the Settings panel both
 * track edits without a reload.
 */
import type { EntityId } from './sim/entity';
import { isEmoteId } from './sim/commands';
import {
  COMBAT_HERALD_NAME,
  LEADERBOARD_HERALD_NAME,
  type ActionAnimationMessage,
  type SpeechMessage,
} from './net/protocol';

/** localStorage key the ignore set persists under (mirrors the other `britbank.*` keys). */
export const IGNORE_LIST_KEY = 'britbank.ignoreList';

/** A structural storage (the browser `localStorage`, or a fake in tests). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The ambient `localStorage`, or `null` when it is absent/blocked (private mode, node). */
function ambientStorage(): StorageLike | null {
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** The case-insensitive, trimmed match key for a name; `''` for a blank/whitespace name. */
export function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Whether `name` is a legitimate ignore target for the viewer whose own display
 * name is `ownName`: a non-blank name that is not (case-insensitively) the viewer's
 * own, and not a broadcast system herald. The local player must never be able to
 * ignore themselves - the filter also guards this by entity id
 * ({@link shouldSuppressSpeech}), but the add flow guards it by name here so the
 * "Ignore <name>" action is never even offered for yourself. The system heralds are
 * vetoed here too: {@link shouldSuppressSpeech} never suppresses them, so adding one
 * would only create a persistable dead entry that suppresses nothing.
 */
export function canIgnoreName(name: string, ownName: string): boolean {
  const key = normalizeName(name);
  if (key.length === 0 || key === normalizeName(ownName)) return false;
  if (key === normalizeName(LEADERBOARD_HERALD_NAME) || key === normalizeName(COMBAT_HERALD_NAME)) {
    return false;
  }
  return true;
}

/** The handle `main.ts`, the chat filter, and the Settings panel drive the list through. */
export interface IgnoreListHandle {
  /** Add `name` to the ignore set (trimmed; blank is a no-op; case-insensitive dedupe). */
  add(name: string): void;
  /** Remove `name` from the ignore set (case-insensitive); a no-op if it is absent. */
  remove(name: string): void;
  /** Whether `name` is ignored (case-insensitive, trimmed). */
  has(name: string): boolean;
  /** Alias of {@link has}, reading naturally at the speech-filter call site. */
  isIgnored(name: string): boolean;
  /** The ignored display names, sorted case-insensitively for a stable UI list. */
  list(): string[];
  /**
   * Subscribe to changes (an add/remove that actually mutated the set). Returns an
   * unsubscribe. Lets the live filter and the Settings section both track edits with
   * no reload - the subscriber re-reads {@link list}/{@link has} on each notification.
   */
  subscribe(listener: () => void): () => void;
}

/** Options for {@link createIgnoreList}; `storage` is injected by tests. */
export interface IgnoreListOptions {
  /** The backing store; defaults to the ambient `localStorage` (session-only if absent). */
  storage?: StorageLike | null;
}

/** Parse the persisted value into normalized display names, tolerating any malformed shape. */
function loadNames(store: StorageLike | null): Map<string, string> {
  const byKey = new Map<string, string>();
  if (!store) return byKey;
  try {
    const raw = store.getItem(IGNORE_LIST_KEY);
    if (!raw) return byKey;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return byKey;
    for (const entry of parsed) {
      // Skip anything that is not a usable name: non-strings, and blank/whitespace.
      if (typeof entry !== 'string') continue;
      const display = entry.trim();
      const key = display.toLowerCase();
      if (key.length === 0) continue;
      if (!byKey.has(key)) byKey.set(key, display);
    }
  } catch {
    // Malformed JSON / storage failure: fall back to an empty list rather than throw.
  }
  return byKey;
}

/**
 * Create an ignore list backed by `localStorage` (or an injected store). The set is
 * keyed by the case-insensitive name, keeping the first-seen casing for display.
 */
export function createIgnoreList(opts: IgnoreListOptions = {}): IgnoreListHandle {
  const store = opts.storage === undefined ? ambientStorage() : opts.storage;
  // key (lowercased+trimmed) -> display name (trimmed, first-seen casing).
  const names = loadNames(store);
  const listeners = new Set<() => void>();

  const persist = (): void => {
    if (!store) return;
    try {
      store.setItem(IGNORE_LIST_KEY, JSON.stringify([...names.values()]));
    } catch {
      /* storage unavailable (private mode / disabled); the choice is session-only. */
    }
  };

  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  return {
    add(name) {
      const display = name.trim();
      const key = display.toLowerCase();
      if (key.length === 0 || names.has(key)) return;
      names.set(key, display);
      persist();
      notify();
    },
    remove(name) {
      const key = normalizeName(name);
      if (!names.delete(key)) return;
      persist();
      notify();
    },
    has(name) {
      return names.has(normalizeName(name));
    },
    isIgnored(name) {
      return names.has(normalizeName(name));
    },
    list() {
      return [...names.values()].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      );
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * Whether an inbound {@link SpeechMessage} should be **suppressed** for this viewer
 * because its speaker is ignored. Only ordinary player/NPC chat is ever ignorable;
 * this never suppresses:
 * - the local player's own lines (matched by entity id - and, since every private
 *   herald reply the server routes over the *recipient's* own entity, this also
 *   protects the `/top` standings, the combat "attacking you!" alert, and the
 *   moderator notices), nor
 * - a **system** action/emote line (the award emote, the dice-roll emote, the duel
 *   count float), nor
 * - a broadcast system herald, recognised by its stamped name.
 *
 * A **player-authored** action line is the one exception to the action exemption: the
 * chat-triggered emote (`*dances*`, ADR-0387) is ordinary player chat wearing emote
 * styling, so the server stamps it {@link SpeechMessage.playerAction} and it falls
 * through to the ignore gate below - `/ignore <name>` hides a dance spammer's line on
 * both surfaces, exactly as it hid it before the line was restyled.
 *
 * Everything else is gated on {@link IgnoreListHandle.isIgnored} by the speaker's
 * server-stamped name. Pure so the filter decision is unit-testable away from the
 * live socket wiring in `main.ts`.
 */
export function shouldSuppressSpeech(
  speech: Pick<SpeechMessage, 'entityId' | 'name' | 'action' | 'playerAction'>,
  playerId: EntityId | undefined,
  isIgnored: (name: string) => boolean,
): boolean {
  if (playerId !== undefined && speech.entityId === playerId) return false;
  if (speech.action === true && speech.playerAction !== true) return false;
  if (speech.name === LEADERBOARD_HERALD_NAME || speech.name === COMBAT_HERALD_NAME) return false;
  return isIgnored(speech.name);
}

/**
 * Whether an inbound {@link ActionAnimationMessage} should be **suppressed** for this
 * viewer because its actor is ignored - the animation half of the ignore filter, so
 * `/ignore <name>` silences a dance spammer's *gesture* as well as their text
 * (ADR-0387). Client-side and per-viewer like the speech filter: the server keeps
 * broadcasting, this is only the local decision to hide.
 *
 * Deliberately narrow - **only a player emote clip** ({@link isEmoteId}: bow / salute /
 * dance) is ever hidden. Combat swings, get-hit flinches, deaths, storm and arena clips
 * must always render: hiding a swing or a death would desync what the viewer
 * understands about the world, whereas a missing bow costs them nothing. The local
 * player's own gestures are never hidden (matched by entity id, the speech filter's
 * guard). `actorName` is the actor's replicated display name - `undefined` (an actor
 * the read-model has not seen) is never suppressed, since ignoring is by name.
 */
export function shouldSuppressActionAnimation(
  msg: Pick<ActionAnimationMessage, 'entityId' | 'clip'>,
  actorName: string | undefined,
  playerId: EntityId | undefined,
  isIgnored: (name: string) => boolean,
): boolean {
  if (playerId !== undefined && msg.entityId === playerId) return false;
  if (!isEmoteId(msg.clip)) return false;
  return actorName !== undefined && isIgnored(actorName);
}
